import React, { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, Save, RefreshCw, X, CheckCircle2, AlertCircle, Copy, Eye, EyeOff, Power } from 'lucide-react';

const S = {
  page: { fontFamily: 'Manrope, sans-serif' },
  h1: { fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E' },
  sub: { fontSize: '13px', color: '#6E7F5B', marginBottom: '20px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px' },
  card: { background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '16px' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' },
  iconBox: { width: '40px', height: '40px', borderRadius: '10px', background: '#F6F1E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 },
  name: { fontWeight: 700, fontSize: '14px', color: '#1F3A2E', lineHeight: 1.2 },
  statusChip: (color) => ({ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', display: 'inline-flex', alignItems: 'center', gap: '3px', ...color }),
  meta: { fontSize: '11px', color: '#6E7F5B', marginTop: '2px' },
  btnRow: { display: 'flex', gap: '6px', marginTop: '12px' },
  btn: { flex: 1, padding: '7px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '8px', border: '1px solid #D8D1C6', background: 'white', color: '#1F3A2E', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px' },
  btnPrimary: { background: '#1F3A2E', color: 'white', borderColor: '#1F3A2E' },
  label: { fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: '5px' },
  input: { width: '100%', padding: '9px 11px', fontSize: '13px', border: '1.5px solid #D8D1C6', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', fontFamily: 'Manrope, sans-serif' },
  textarea: { width: '100%', padding: '9px 11px', fontSize: '13px', border: '1.5px solid #D8D1C6', borderRadius: '8px', outline: 'none', boxSizing: 'border-box', fontFamily: 'Manrope, sans-serif', minHeight: '70px', resize: 'vertical' },
};

const STATUS_OK = { background: '#dcfce7', color: '#166534' };
const STATUS_PENDING = { background: '#fef3c7', color: '#92400e' };
const STATUS_ERR = { background: '#fee2e2', color: '#991b1b' };

export default function AdminChannels() {
  const { apiHeaders, language, BACKEND_URL } = useApp();
  const [channels, setChannels] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [syncAllLoading, setSyncAllLoading] = useState(false);

  useEffect(() => {
    document.title = language === 'tr' ? 'Kanal Yönetimi | Amorium Admin' : 'Channel Manager | Amorium Admin';
    fetchAll();
  }, [language]);

  const fetchAll = async () => {
    try {
      const [chRes, roomRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/channels`, apiHeaders()),
        axios.get(`${BACKEND_URL}/api/rooms`),
      ]);
      setChannels(chRes.data);
      setRooms(roomRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yüklenemedi');
    }
    setLoading(false);
  };

  const openEdit = async (ch) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/channels/${ch._id}`, apiHeaders());
      setForm(res.data);
      setEditingId(ch._id);
      setShowPw(false);
    } catch (err) {
      toast.error('Kanal yüklenemedi');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        ical_import_url: form.ical_import_url || '',
        username: form.username || '',
        password: form.password || '',
        notes: form.notes || '',
        room_id: form.room_id || null,
      };
      await axios.put(`${BACKEND_URL}/api/admin/channels/${editingId}`, payload, apiHeaders());
      toast.success(language === 'tr' ? 'Kanal güncellendi' : 'Channel saved');
      setEditingId(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    }
    setSaving(false);
  };

  const handleSync = async (ch) => {
    if (!ch.ical_import_url || !ch.room_id) {
      toast.error(language === 'tr' ? 'Önce iCal URL ve oda seçin' : 'Set iCal URL and room first');
      return;
    }
    setSyncingId(ch._id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/channels/${ch._id}/sync`, {}, apiHeaders());
      toast.success(`${ch.name}: ${res.data.blocked_count} tarih bloke edildi`);
      fetchAll();
    } catch (err) {
      toast.error(`${ch.name}: ${err.response?.data?.detail || 'Senkron hatası'}`);
    }
    setSyncingId(null);
  };

  const handleSyncAll = async () => {
    setSyncAllLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/channels/sync-all`, {}, apiHeaders());
      toast.success(language === 'tr' ? `${res.data.synced} kanal senkronize edildi` : `${res.data.synced} channels synced`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata');
    }
    setSyncAllLoading(false);
  };

  const copyExportUrl = (roomId) => {
    const url = `${BACKEND_URL}/api/ical/room/${roomId}.ics`;
    navigator.clipboard.writeText(url);
    toast.success(language === 'tr' ? 'Bağlantı kopyalandı' : 'URL copied');
  };

  const formatTime = (iso) => {
    if (!iso) return language === 'tr' ? 'Hiç' : 'Never';
    return new Date(iso).toLocaleString(language === 'tr' ? 'tr-TR' : 'en-US', { dateStyle: 'short', timeStyle: 'short' });
  };

  return (
    <div style={S.page} data-testid="admin-channels-page">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={S.h1}>{language === 'tr' ? 'Kanal Yönetimi' : 'Channel Manager'}</h1>
          <p style={S.sub}>{language === 'tr' ? '14 OTA kanalı — iCal senkronu + erişim bilgileri' : '14 OTA channels — iCal sync + credentials'}</p>
        </div>
        <button
          onClick={handleSyncAll}
          disabled={syncAllLoading}
          style={{ ...S.btn, ...S.btnPrimary, flex: 'none', padding: '10px 16px', fontSize: '13px' }}
          data-testid="sync-all-channels-btn"
        >
          <RefreshCw size={14} style={syncAllLoading ? { animation: 'spin 1s linear infinite' } : {}} />
          {syncAllLoading ? (language === 'tr' ? 'Senkronlanıyor...' : 'Syncing...') : (language === 'tr' ? 'Tümünü Senkronla' : 'Sync All')}
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: '#EFE7DA', border: '1px solid #D8D1C6', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px', fontSize: '13px', color: '#5A5450', lineHeight: 1.5 }}>
        <strong style={{ color: '#1F3A2E' }}>ℹ️ {language === 'tr' ? 'Nasıl Kullanılır:' : 'How it Works:'}</strong>{' '}
        {language === 'tr'
          ? "Her OTA panelinden iCal export URL'sini alın → Aşağıdaki ilgili karta yapıştırın → İlgili odayı seçin → Kanalı aktive edin → Senkronla. Otomatik olarak dolu tarihler bloklanır. Tersine bizden OTA'lara push için: Kart altındaki 'iCal Çıkışı' linkini kopyalayıp OTA paneline yapıştırın."
          : "Get iCal export URL from each OTA panel → paste into matching card → select room → enable → sync. Bookings auto-block dates. To push our calendar to OTAs: copy the 'iCal Export' URL below and paste into the OTA panel."}
      </div>

      {loading ? (
        <div>{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
      ) : (
        <div style={S.grid}>
          {channels.map(ch => {
            const isEnabled = ch.enabled;
            const hasConfig = ch.ical_import_url && ch.room_id;
            const status = isEnabled && hasConfig ? STATUS_OK : (ch.last_sync_status && ch.last_sync_status !== 'success' ? STATUS_ERR : STATUS_PENDING);
            const statusLabel = isEnabled && hasConfig ? (language === 'tr' ? 'Aktif' : 'Active') : (hasConfig ? (language === 'tr' ? 'Pasif' : 'Disabled') : (language === 'tr' ? 'Kurulum Bekliyor' : 'Setup Pending'));
            return (
              <div key={ch._id} style={S.card} data-testid={`channel-card-${ch.key}`}>
                <div style={S.cardHead}>
                  <div style={S.iconBox}>{ch.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={S.name}>{ch.name}</p>
                    <span style={S.statusChip(status)}>
                      {isEnabled && hasConfig ? <CheckCircle2 size={10} /> : <AlertCircle size={10} />} {statusLabel}
                    </span>
                  </div>
                </div>
                <div style={S.meta}>
                  {language === 'tr' ? 'Son senkron:' : 'Last sync:'} {formatTime(ch.last_sync_at)}
                  {ch.last_sync_blocked_count > 0 && <span> · {ch.last_sync_blocked_count} {language === 'tr' ? 'tarih' : 'dates'}</span>}
                </div>
                {ch.last_sync_status && ch.last_sync_status !== 'success' && (
                  <div style={{ marginTop: '6px', fontSize: '11px', color: '#991b1b', background: '#fef2f2', padding: '5px 8px', borderRadius: '6px', wordBreak: 'break-word' }}>
                    {ch.last_sync_status}
                  </div>
                )}
                <div style={S.btnRow}>
                  <button style={S.btn} onClick={() => openEdit(ch)} data-testid={`edit-channel-${ch.key}`}>
                    <Link2 size={12} /> {language === 'tr' ? 'Ayarlar' : 'Settings'}
                  </button>
                  <button
                    style={{ ...S.btn, ...(hasConfig && isEnabled ? S.btnPrimary : { opacity: 0.5, cursor: 'not-allowed' }) }}
                    onClick={() => hasConfig && isEnabled && handleSync(ch)}
                    disabled={!hasConfig || !isEnabled || syncingId === ch._id}
                    data-testid={`sync-channel-${ch.key}`}
                  >
                    <RefreshCw size={12} style={syncingId === ch._id ? { animation: 'spin 1s linear infinite' } : {}} />
                    {syncingId === ch._id ? '...' : (language === 'tr' ? 'Senkronla' : 'Sync')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* iCal Export URLs */}
      {!loading && rooms.length > 0 && (
        <div style={{ marginTop: '32px', background: 'white', border: '1px solid #EFE7DA', borderRadius: '14px', padding: '18px' }}>
          <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '20px', fontWeight: 600, color: '#1F3A2E', marginBottom: '6px' }}>
            {language === 'tr' ? '📤 iCal Çıkış Bağlantıları (OTA panellerine yapıştırın)' : '📤 iCal Export URLs (paste into OTA panels)'}
          </h2>
          <p style={{ fontSize: '12px', color: '#6E7F5B', marginBottom: '12px' }}>
            {language === 'tr' ? 'Bu URL’ler herkese açıktır — sadece bu odaya ait rezervasyon ve manuel blokları içerir.' : 'These URLs are public — contain only this room\'s reservations and manual blocks.'}
          </p>
          {rooms.map(room => (
            <div key={room._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #F6F1E7' }}>
              <span style={{ fontSize: '13px', color: '#1F3A2E', fontWeight: 600, minWidth: '160px', flexShrink: 0 }}>{room.name}</span>
              <code style={{ flex: 1, fontSize: '11px', color: '#6E7F5B', background: '#F6F1E7', padding: '5px 8px', borderRadius: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {`${BACKEND_URL}/api/ical/room/${room._id}.ics`}
              </code>
              <button style={S.btn} onClick={() => copyExportUrl(room._id)} data-testid={`copy-ical-export-${room._id}`}>
                <Copy size={12} /> {language === 'tr' ? 'Kopyala' : 'Copy'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingId && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setEditingId(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '1rem', overflowY: 'auto' }}
        >
          <div style={{ background: 'white', borderRadius: '20px', width: '100%', maxWidth: '560px', padding: '24px', margin: '20px auto', fontFamily: 'Manrope, sans-serif' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px' }}>
              <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '22px', fontWeight: 600, color: '#1F3A2E', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '24px' }}>{form.icon}</span> {form.name}
              </h2>
              <button onClick={() => setEditingId(null)} style={{ background: '#F6F1E7', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <X size={16} style={{ color: '#6E7F5B' }} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Enabled toggle */}
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: '#F6F1E7', borderRadius: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  data-testid="channel-enabled-toggle"
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 700, color: '#1F3A2E' }}><Power size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{language === 'tr' ? 'Kanalı Aktif Et' : 'Enable Channel'}</p>
                  <p style={{ fontSize: '11px', color: '#6E7F5B' }}>{language === 'tr' ? 'Aktif kanallar “Tümünü Senkronla” ile birlikte çalışır' : 'Active channels are included in "Sync All"'}</p>
                </div>
              </label>

              {/* Room mapping */}
              <div>
                <label style={S.label}>{language === 'tr' ? 'Bağlı Oda' : 'Linked Room'}</label>
                <select
                  value={form.room_id || ''}
                  onChange={e => setForm(f => ({ ...f, room_id: e.target.value }))}
                  style={S.input}
                  data-testid="channel-room-select"
                >
                  <option value="">{language === 'tr' ? '— Oda Seçin —' : '— Select Room —'}</option>
                  {rooms.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                </select>
              </div>

              {/* iCal URL */}
              <div>
                <label style={S.label}>{language === 'tr' ? 'iCal İçe Aktarma URL' : 'iCal Import URL'} <span style={{ color: '#dc2626' }}>*</span></label>
                <input
                  value={form.ical_import_url || ''}
                  onChange={e => setForm(f => ({ ...f, ical_import_url: e.target.value }))}
                  placeholder="https://admin.booking.com/hotel/.../calendar.ics?key=..."
                  style={S.input}
                  data-testid="channel-ical-url"
                />
                <p style={{ fontSize: '11px', color: '#6E7F5B', marginTop: '4px' }}>
                  {language === 'tr' ? 'OTA extranet panelinden alınan iCal export linki' : 'iCal export link from the OTA extranet panel'}
                </p>
              </div>

              {/* Credentials */}
              <div style={{ borderTop: '1px solid #EFE7DA', paddingTop: '14px' }}>
                <p style={{ fontSize: '12px', color: '#6E7F5B', marginBottom: '10px' }}>
                  {language === 'tr' ? 'Erişim Bilgileri (referans için, şifre AES ile şifrelenir)' : 'Access Credentials (for reference, password encrypted at rest)'}
                </p>
                <div>
                  <label style={S.label}>{language === 'tr' ? 'Kullanıcı Adı / E-posta' : 'Username / Email'}</label>
                  <input
                    value={form.username || ''}
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                    style={S.input}
                    data-testid="channel-username"
                  />
                </div>
                <div style={{ marginTop: '12px' }}>
                  <label style={S.label}>{language === 'tr' ? 'Şifre' : 'Password'} {form.has_password && !form.password && <span style={{ color: '#6E7F5B', fontWeight: 400 }}>({language === 'tr' ? 'mevcut — boş bırakırsanız korunur' : 'set — leave blank to keep'})</span>}</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPw ? 'text' : 'password'}
                      value={form.password || ''}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      placeholder={form.has_password ? '••••••••' : ''}
                      style={{ ...S.input, paddingRight: '40px' }}
                      data-testid="channel-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw(p => !p)}
                      style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', color: '#6E7F5B', padding: '4px' }}
                    >
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label style={S.label}>{language === 'tr' ? 'Notlar' : 'Notes'}</label>
                <textarea
                  value={form.notes || ''}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder={language === 'tr' ? 'Komisyon oranı, ekstra bilgi...' : 'Commission rate, extra info...'}
                  style={S.textarea}
                  data-testid="channel-notes"
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', paddingTop: '4px' }}>
                <button
                  style={{ ...S.btn, ...S.btnPrimary, padding: '12px', fontSize: '13px' }}
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="channel-save-btn"
                >
                  <Save size={14} />
                  {saving ? (language === 'tr' ? 'Kaydediliyor...' : 'Saving...') : (language === 'tr' ? 'Kaydet' : 'Save')}
                </button>
                <button style={{ ...S.btn, padding: '12px 16px' }} onClick={() => setEditingId(null)}>
                  {language === 'tr' ? 'İptal' : 'Cancel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
